import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import { localPoint } from '@visx/event';
import { bisector } from 'd3-array';
import { debounce } from 'lodash';
import type { ScaleLinear } from 'd3-scale';
import { useIndustryContext } from '../../../IndustryContextProvider';

interface Props {
  x?: number;
  y?: number;
  height?: number;
  width?: number;
  keys: number[];
  xScaleColumn: ScaleLinear<number, number>;
  xScale: ScaleLinear<number, number>;
  columnWidth: number;
  rowMargin: number;
}

const ChartInteractions = ({
  x,
  y,
  height,
  width,
  keys,
  xScaleColumn,
  xScale,
  columnWidth,
  rowMargin,
}: Props) => {
  const { index, setIndex } = useIndustryContext();
  const theme = useTheme();

  //For hover interactions
  const [hoverIndex, setHoverIndex] = useState<number>(-1);
  const debouncedSetIndex = useMemo(() => debounce(setIndex, 500), [setIndex]);

  const handleClick = (e) => {
    const point = localPoint(e);
    const multipleValue = xScale.invert(point ? point.x : 0);
    const nextIndex = bisector((d) => d).left(keys, multipleValue);
    if (nextIndex !== index && nextIndex <= keys.length - 1) {
      setIndex(nextIndex);
    }
  };

  const handleMove = (e) => {
    const point = localPoint(e);
    const multipleValue = xScale.invert(point ? point.x : 0);
    const nextIndex = bisector((d) => d).left(keys, multipleValue);
    if (nextIndex !== hoverIndex && nextIndex <= keys.length - 1) {
      setHoverIndex(nextIndex);
      debouncedSetIndex.cancel();
      debouncedSetIndex(nextIndex);
    }
  };

  const handleExit = () => {
    setHoverIndex(-1);
    debouncedSetIndex.cancel();
  };

  //Sync hovered index with any index changes
  useEffect(() => {
    setHoverIndex(index);
  }, [setHoverIndex, index]);

  return (
    <>
      {/* MOVE HANDLER */}
      <rect
        x={x}
        y={y}
        height={height}
        width={width && width > 0 ? width : '100%'}
        fill="transparent"
        onMouseMove={handleMove}
        onTouchMove={handleMove}
        onClick={handleClick}
        onTouchEnd={handleClick}
        onTouchCancel={handleExit}
        onMouseLeave={handleExit}
        cursor="pointer"
      />
      {/* SELECTION HIGHLIGHT */}
      {hoverIndex >= 0 && (
        <rect
          y={y}
          height={height}
          fill={theme.color.whiteRGBA(0.1)}
          width={Math.max(columnWidth - rowMargin, 0)}
          transform={`translate(${xScaleColumn(hoverIndex)},0)`}
          pointerEvents={'none'}
        />
      )}
      {/* INDEX HIGHLIGHT */}
      {index >= 0 && (
        <rect
          y={y}
          height={height}
          fill={theme.color.chart01RGBA(0.2)}
          width={Math.max(columnWidth - rowMargin, 0)}
          transform={`translate(${xScaleColumn(index)},0)`}
          pointerEvents={'none'}
        />
      )}
    </>
  );
};

export default ChartInteractions;
